import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import { Breadcrumb } from "@/components/feature/Breadcrumb";
import EpisodeList from "./components/EpisodeList";
import { allWorks } from "@/mocks/works";
import { categories } from "@/mocks/categories";
import { WORK_TYPE_LABEL } from "@/types/catalog";
import { useFavorites } from "@/hooks/useFavorites";

export default function WorkDetailPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const work = allWorks.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const category = categories.find((item) => item.key === type);
  const favorited = work ? isFavorite(work.id) : false;

  useEffect(() => {
    if (!work) {
      document.title = "内容不存在 - 爱妻艺";
      return;
    }
    document.title = `${work.title} - 爱妻艺`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", work.description.slice(0, 120));
  }, [work]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!work) return [];
    return allWorks
      .filter((item) => item.id !== work.id)
      .sort((a, b) => {
        const scoreA =
          (a.type === work.type ? 2 : 0) + a.genres.filter((g) => work.genres.includes(g)).length;
        const scoreB =
          (b.type === work.type ? 2 : 0) + b.genres.filter((g) => work.genres.includes(g)).length;
        return scoreB - scoreA;
      })
      .slice(0, 4);
  }, [work]);

  const handleFavorite = () => {
    if (!work) return;
    const added = toggleFavorite(work.id);
    setToast(added ? "已加入收藏，数据保存在本地浏览器" : "已取消收藏");
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("分享链接：" + url);
    }
  };

  if (!work) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-20 text-center md:px-6">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-4 text-xl font-bold text-foreground-900">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">该作品可能已下架，去看看其他精彩内容吧。</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {categories.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const infoItems = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "类型", value: work.genres.join(" / ") },
    { label: "上映年份", value: String(work.year) },
    { label: "片长/更新", value: work.duration },
    { label: "地区", value: work.region },
  ];

  return (
    <div className="w-full">
      <div className="relative w-full overflow-hidden">
        <div className="h-[260px] w-full md:h-[360px]">
          <img
            src={work.banner}
            alt={`${work.title}剧照`}
            title={`${work.title} - 爱妻艺影视推荐`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/20"></div>

        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto w-full max-w-[1280px] px-4 pb-6 md:px-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end">
              <div className="hidden h-52 w-36 shrink-0 overflow-hidden rounded-lg border-4 border-background-50/20 sm:block">
                <img
                  src={work.cover}
                  alt={`${work.title}封面`}
                  title={`${work.title} - 爱妻艺影视推荐`}
                  className="h-full w-full object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <span className="inline-flex items-center gap-1 rounded-sm bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">
                  <i className="ri-bookmark-3-line"></i>
                  {WORK_TYPE_LABEL[work.type]}
                </span>
                <h1 className="mt-2 text-2xl font-bold text-background-50 md:text-4xl">{work.title}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200">
                  <span className="flex items-center gap-1 font-semibold text-accent-400">
                    <i className="ri-star-fill"></i>
                    {work.rating.toFixed(1)}
                  </span>
                  <span>{work.year}</span>
                  <span>{work.region}</span>
                  <span>{work.duration}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line"></i>
                    热度 {work.views}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {work.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-50/30 px-2.5 py-0.5 text-xs text-background-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                      favorited
                        ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                        : "bg-background-50 text-foreground-900 hover:bg-background-100"
                    }`}
                  >
                    <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                    {favorited ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视分类", to: category?.path ?? "/" },
            { label: category?.name ?? WORK_TYPE_LABEL[work.type], to: category?.path },
            { label: work.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                {work.description}
              </p>
              <div className="mt-4 flex items-center gap-2 rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-500">
                <i className="ri-information-line text-primary-600"></i>
                本站为影视资讯与作品展示平台，不提供播放或下载功能。
              </div>
            </section>

            {work.episodes && work.episodes.length > 0 && (
              <EpisodeList episodes={work.episodes} />
            )}

            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="mb-4 flex items-end justify-between">
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <span className="h-5 w-1 rounded-full bg-primary-500"></span>
                  相关推荐
                </h2>
                <Link
                  to={category?.path ?? "/category/movie"}
                  className="flex cursor-pointer items-center gap-0.5 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
                >
                  更多
                  <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                {related.map((item) => (
                  <WorkCard key={item.id} work={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-4">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-information-2-line text-primary-600"></i>
                基础信息
              </h2>
              <dl className="mt-3 flex flex-col gap-3 text-sm">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex gap-3 border-b border-background-100 pb-3 last:border-0 last:pb-0">
                    <dt className="w-20 shrink-0 text-foreground-400">{item.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="rounded-lg bg-primary-700 p-5 text-background-100">
              <h3 className="text-base font-bold text-background-50">发现更多好作品</h3>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                爱妻艺持续更新电影、电视剧、综艺，用清晰分类帮你快速找到想看的内容。
              </p>
              <Link
                to="/"
                className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                回到首页
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </section>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}